import { useEffect, useRef, useState } from "react";
import { HeartHandshake, Building2, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { http, errText } from "@/lib/api";
import { COMPANY, ROLES } from "@/lib/content";
import { EmailText } from "./EmailText";
import { Eyebrow, Reveal } from "./Reveal";

const inp = "w-full px-3.5 py-3 rounded-xl border border-[#154F49]/30 bg-white text-sm text-[#1A2E2B] placeholder:text-[#6B7D78] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#CDA34F] aria-[invalid=true]:border-red-700";
const CARE_TYPES = ["Personal care", "Dementia support", "Mobility / transfers", "Medication reminders", "Companionship", "Not sure yet"];

const F = ({ id, label, req, children, err }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-bold text-[#154F49] mb-1">{label}{req && <span className="text-[#9B742A]" aria-hidden> *</span>}</label>
    {children}
    {err && <p id={`${id}-err`} className="text-xs font-semibold text-red-800 mt-1">{err}</p>}
  </div>
);
const In = ({ kind, name, label, req, type = "text", err, ...rest }) => (
  <F id={`${kind}-${name}`} label={label} req={req} err={err}>
    <input id={`${kind}-${name}`} name={name} type={type} required={req} aria-invalid={err ? true : undefined} aria-describedby={err ? `${kind}-${name}-err` : undefined} data-testid={`${kind}-${name}`} className={inp} {...rest} />
  </F>
);

const CareFields = ({ e }) => (
  <>
    <In kind="care" name="name" label="Your name" req err={e.name} autoComplete="name" />
    <In kind="care" name="email" label="Email" type="email" req err={e.email} autoComplete="email" />
    <In kind="care" name="phone" label="Phone" type="tel" req err={e.phone} autoComplete="tel" />
    <In kind="care" name="location" label="City or ZIP code" req err={e.location} />
    <F id="care-care_type" label="Type of care needed" req err={e.care_type}>
      <select id="care-care_type" name="care_type" required defaultValue="" aria-invalid={e.care_type ? true : undefined} data-testid="care-care_type" className={inp}>
        <option value="" disabled>Select…</option>{CARE_TYPES.map((c) => <option key={c}>{c}</option>)}
      </select>
    </F>
    <In kind="care" name="start_date" label="Preferred start date" type="date" />
    <fieldset className="sm:col-span-2">
      <legend className="block text-xs font-bold text-[#154F49] mb-2">Preferred contact method<span className="text-[#9B742A]" aria-hidden> *</span></legend>
      <div className="flex flex-wrap gap-4">{["Phone call", "Text message", "Email"].map((m) => (
        <label key={m} className="inline-flex items-center gap-2 text-sm"><input type="radio" name="contact_method" value={m} className="w-4 h-4 accent-[#154F49]" data-testid={`care-contact-${m.split(" ")[0].toLowerCase()}`} />{m}</label>
      ))}</div>
      {e.contact_method && <p className="text-xs font-semibold text-red-800 mt-1">{e.contact_method}</p>}
    </fieldset>
  </>
);

const StaffFields = ({ e }) => (
  <>
    <In kind="staff" name="facility" label="Facility name" req err={e.facility} />
    <In kind="staff" name="contact_person" label="Contact person" req err={e.contact_person} />
    <In kind="staff" name="email" label="Email" type="email" req err={e.email} />
    <In kind="staff" name="phone" label="Phone" type="tel" req err={e.phone} />
    <In kind="staff" name="location" label="Facility location (city, state)" req err={e.location} />
    <In kind="staff" name="count" label="Number of staff needed" type="number" min="1" req err={e.count} />
    <fieldset className="sm:col-span-2">
      <legend className="block text-xs font-bold text-[#154F49] mb-2">Staff roles needed<span className="text-[#9B742A]" aria-hidden> *</span></legend>
      <div className="flex flex-wrap gap-x-5 gap-y-2">{ROLES.map((r) => (
        <label key={r.abbr} className="inline-flex items-center gap-2 text-sm"><input type="checkbox" name="roles" value={r.abbr} className="w-4 h-4 accent-[#154F49]" data-testid={`staff-role-${r.abbr.toLowerCase()}`} />{r.abbr}</label>
      ))}</div>
      {e.roles && <p className="text-xs font-semibold text-red-800 mt-1">{e.roles}</p>}
    </fieldset>
    <In kind="staff" name="shifts" label="Shift schedule (e.g. nights, 7p–7a, weekends)" req err={e.shifts} />
    <In kind="staff" name="start_date" label="Start date" type="date" />
  </>
);

const REQUIRED = {
  care: { name: "Please enter your name.", location: "Please enter a city or ZIP code.", care_type: "Please choose the type of care.", contact_method: "Please choose how we should contact you." },
  staff: { facility: "Please enter the facility name.", contact_person: "Please enter a contact person.", location: "Please enter the facility location.", count: "Please enter how many staff you need.", shifts: "Please describe the shift schedule.", roles: "Please choose at least one role." },
};

function check(kind, d) {
  const e = {};
  Object.entries(REQUIRED[kind]).forEach(([k, m]) => { if (!(Array.isArray(d[k]) ? d[k].length : String(d[k] || "").trim())) e[k] = m; });
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email || "")) e.email = "Please enter a valid email address.";
  if ((d.phone || "").replace(/\D/g, "").length < 10) e.phone = "Please enter a 10-digit phone number.";
  if (!d.consent) e.consent = "Please agree to be contacted.";
  return e;
}

export const RequestForms = () => {
  const [kind, setKind] = useState("care");
  const [e, setE] = useState({});
  const [state, setState] = useState({ s: "idle" });
  const ref = useRef(null);
  useEffect(() => {
    const onHash = () => { if (window.location.hash === "#request-staff") setKind("staff"); if (window.location.hash === "#request-care") setKind("care"); };
    onHash();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const switchKind = (k) => { setKind(k); setE({}); setState({ s: "idle" }); };
  const submit = async (ev) => {
    ev.preventDefault();
    if (state.s === "sending") return;
    const fd = new FormData(ref.current);
    const d = Object.fromEntries(fd.entries());
    d.roles = fd.getAll("roles");
    d.consent = fd.get("consent") === "yes";
    const errs = check(kind, d);
    setE(errs);
    if (Object.keys(errs).length) { setState({ s: "invalid" }); return; }
    setState({ s: "sending" });
    try {
      const { data } = await http.post("/requests", { ...d, kind }, { timeout: 25000 });
      setState({ s: "ok", ref: data?.reference, mail: data?.notification });
      ref.current?.reset();
    } catch (x) { setState({ s: "fail", msg: errText(x) }); }
  };
  const tab = (k, Icon, label) => (
    <button type="button" role="tab" aria-selected={kind === k} onClick={() => switchKind(k)} data-testid={`request-tab-${k}`}
      className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-full text-sm font-bold transition-colors ${kind === k ? "bg-[#154F49] text-[#FBF8F1]" : "text-[#154F49] hover:bg-[#EDF1E6]"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
  return (
    <section id="request" className="py-20 sm:py-28 bg-[#FFFDF8] border-y border-[#154F49]/10 no-print" data-testid="request-section">
      <span id="request-care" className="block" aria-hidden /><span id="request-staff" className="block" aria-hidden />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-4 space-y-4">
          <Eyebrow>Get started</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#154F49] leading-[1.04]">Tell us what you need.</h2>
          <p className="text-[#465F57] leading-relaxed">Families can request care at home; facilities can request credentialed staff. Our team reviews every request and contacts you to confirm coverage and availability.</p>
          <p className="text-sm text-[#1A2E2B]">Prefer to talk? <a href={COMPANY.phoneHref} className="font-bold text-[#154F49] underline">{COMPANY.phoneDisplay}</a> · <a href={`mailto:${COMPANY.email}`} className="font-bold text-[#154F49] underline"><EmailText /></a></p>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-8">
          <div className="bg-white rounded-3xl border border-[#154F49]/15 p-5 sm:p-8 shadow-[0_30px_60px_-40px_rgba(21,79,73,0.5)]">
            <div role="tablist" aria-label="Request type" className="flex gap-1 p-1 rounded-full bg-[#FBF8F1] border border-[#154F49]/15 mb-6">
              {tab("care", HeartHandshake, "Request Care")}{tab("staff", Building2, "Request Staff")}
            </div>
            <form ref={ref} onSubmit={submit} noValidate className="grid sm:grid-cols-2 gap-4" data-testid={`request-form-${kind}`} key={kind}>
              <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
              {kind === "care" ? <CareFields e={e} /> : <StaffFields e={e} />}
              <div className="sm:col-span-2">
                <label htmlFor={`${kind}-message`} className="block text-xs font-bold text-[#154F49] mb-1">Message (optional)</label>
                <textarea id={`${kind}-message`} name="message" rows={3} maxLength={2000} className={inp} data-testid={`${kind}-message`} />
                <p className="text-xs text-[#465F57] mt-1">Please don't include medical records, diagnoses, Social Security numbers or other sensitive identification.</p>
              </div>
              <label className="sm:col-span-2 flex items-start gap-3 text-sm text-[#1A2E2B] cursor-pointer">
                <input type="checkbox" name="consent" value="yes" className="mt-0.5 w-5 h-5 accent-[#154F49] shrink-0" data-testid={`${kind}-consent`} aria-invalid={e.consent ? true : undefined} />
                <span>I agree that Elevion Care Network LLC may contact me about this request by my chosen method. <span className="text-[#9B742A]">*</span>{e.consent && <span className="block text-xs font-semibold text-red-800 mt-1">{e.consent}</span>}</span>
              </label>
              <div className="sm:col-span-2 space-y-3" aria-live="polite">
                {state.s === "invalid" && <p role="alert" className="text-sm font-semibold text-red-800" data-testid="request-invalid">Please fix the highlighted fields.</p>}
                {state.s === "fail" && <p role="alert" className="flex gap-2 text-sm font-semibold text-red-800" data-testid="request-error"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {state.msg} You can also call {COMPANY.phoneDisplay}.</p>}
                {state.s === "ok" && (
                  <div className="flex gap-3 p-4 rounded-2xl bg-emerald-50 border border-[#154F49] text-sm" data-testid="request-success">
                    <CheckCircle2 className="w-5 h-5 text-[#154F49] shrink-0" />
                    <p><strong className="text-[#154F49]">Request received — reference {state.ref}.</strong> {state.mail === "sent" ? "Our team has been notified by email and will contact you." : "It's saved for our team. If you don't hear back within one business day, please call (202) 710-0131."}</p>
                  </div>
                )}
                <button type="submit" disabled={state.s === "sending"} className="btn-primary px-7 py-4 disabled:opacity-60" data-testid="request-submit-btn">
                  {state.s === "sending" ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : kind === "care" ? "Send care request" : "Send staffing request"}
                </button>
              </div>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
